/**
 * Debug Configuration for Development vs Production
 *
 * Usage: DEBUG.log('GPS', '🔍 GPS Data:', data);
 * Result: Only logs when DEBUG=true in env or specific category enabled
 * Production: All debug logging is disabled and stripped from builds
 */

interface DebugConfig {
  ALL: boolean;
  GPS: boolean;
  POWER: boolean;
  TELEMETRY: boolean;
  EMBEDDING: boolean;
  AUTH: boolean;
  SCRIPT: boolean;
  CONSENT: boolean;
  CALIBRATION: boolean;
  log: (category: keyof DebugConfig, ...args: any[]) => void;
}

// Environment checks
const isProduction = process.env.REACT_APP_ENVIRONMENT === 'production';
const isStaging = process.env.REACT_APP_SENTRY_ENVIRONMENT === 'staging';
const isDevelopment = process.env.NODE_ENV === 'development';

export const DEBUG: DebugConfig = {
  // Force disable all debug logging in production
  ALL: isProduction ? false : process.env.REACT_APP_DEBUG === 'true',
  GPS: isProduction ? false : process.env.REACT_APP_DEBUG_GPS === 'true',
  POWER: isProduction ? false : process.env.REACT_APP_DEBUG_POWER === 'true',
  TELEMETRY: isProduction ? false : process.env.REACT_APP_DEBUG_TELEMETRY === 'true',
  EMBEDDING: isProduction ? false : process.env.REACT_APP_DEBUG_EMBEDDING === 'true',
  AUTH: isProduction ? false : process.env.REACT_APP_DEBUG_AUTH === 'true',
  SCRIPT: isProduction ? false : process.env.REACT_APP_DEBUG_SCRIPT === 'true',
  CONSENT: isProduction ? false : process.env.REACT_APP_DEBUG_CONSENT === 'true',
  CALIBRATION: isProduction ? false : process.env.REACT_APP_DEBUG_CALIBRATION === 'true',

  // Helper function for conditional logging - no-op in production
  log: function (category: keyof DebugConfig, ...args: any[]) {
    if (isProduction) {
      // No-op in production - this will be tree-shaken out
      return;
    }
    if (this.ALL || this[category]) {
      // eslint-disable-next-line no-console
      console.log(...args);
    }
  },
};

// Disable all debug logging in staging environment
if (isStaging) {
  (DEBUG as any).ALL = false;
  (DEBUG as any).GPS = false;
  (DEBUG as any).POWER = false;
  (DEBUG as any).TELEMETRY = false;
  (DEBUG as any).EMBEDDING = false;
  (DEBUG as any).AUTH = false;
  (DEBUG as any).SCRIPT = false;
  (DEBUG as any).CONSENT = false;
  (DEBUG as any).CALIBRATION = false;
}

// Quick enable all for development
if (isDevelopment && !process.env.REACT_APP_DEBUG && !isStaging) {
  // Allow console logs in development by default
  (DEBUG as any).ALL = true;
}

/**
 * Check if prototypes should be enabled
 * Prototypes are only available in staging and development, not production
 */
export const isPrototypesEnabled = (): boolean => {
  return !isProduction;
};
